import type { TEnrichedWell } from '@repo/well-list';
import { WellListPanel } from '@repo/well-list';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TCreateWidgetOptions, TabEntity } from 'src/entities/tab/TabEntity';
import { useService } from 'src/packages/di';
import { useRefs } from 'src/packages/shared/hooks/useRefsArray';

import { ContextMenuButton } from '../context-menu-button/ContextMenuButton';
import { FiltersWidgetEntity } from '../filters-widget/FiltersWidget.entity';
import { GroupSelectForWellList } from '../group-select/GroupSelectForWellList';
import { FilterButton } from '../workbench/filter-button/FilterButton';
import { PinWidgetButton } from '../workbench/pin-widget-button/PinWidgetButton';
import type { TTabTemplateWithGSProps } from '../workspace/types';

import styles from './WellListPanelAsPopup.module.scss';

type Props = {
  actualTab: TabEntity | null;
  onClosePopup: () => void;
  onCreateNewTabAccept?: (template: TTabTemplateWithGSProps, wellId: number | null, customTabName?: string) => void;
};

type WellSelectOptions = {
  reason: string;
};

const hasSelectReason = (options: unknown): options is WellSelectOptions => {
  if (!options || typeof options !== 'object') {
    return false;
  }

  return 'reason' in options && typeof options.reason === 'string';
};

export const WellListPanelAsPopup = observer(function WellListPanelAsPopup({
  actualTab,
  onClosePopup,
  onCreateNewTabAccept,
}: Props) {
  const { refAt, valueAt, clearRefs } = useRefs<HTMLDivElement, number>();
  const [isGroupSelectOpen, setIsGroupSelectOpen] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);
  const notifications = useService('notifications');
  const language = useService('language');
  const { theme } = useService('theme');

  const { t } = useTranslation();
  const isPinned = !!actualTab?.wellListPanelState?.isPinned;

  const createFilterWidget = useCallback(() => {
    const groupId = actualTab?.wellListPanelState?.groupId;

    if (!groupId) {
      const emptyGroupId = actualTab?.groupSelectStore.groupManager.findEmptyGroup();
      if (emptyGroupId === null) {
        notifications.showErrorMessageT('errors:noAvailableGroup');
        return;
      }
      actualTab?.createWellsFilterWidget({ groupId: emptyGroupId });
      actualTab?.wellListPanelState.updateWellListPanelState({ groupId: emptyGroupId });
    } else {
      actualTab.createWellsFilterWidget({ groupId: groupId });
    }
  }, [actualTab, notifications]);

  useEffect(() => {
    if (actualTab) {
      actualTab.wellListPanelStore.setRefs(valueAt, clearRefs);
    }
  }, [actualTab, valueAt, clearRefs]);

  if (!actualTab) {
    return null;
  }

  const {
    widgets,
    groupSelectStore,
    wellListPanelState,
    createWellLogsWidget,
    createOperationalParametersWidget,
    createWellAnalyseWidget,
    createDirectionalDrillingWidget,
    createImageBroadcastWidget,
    createVideobroadcastWidget,
    createDayDepthChartWidget,
    createHeatmapWidget,
    createDrillingMultiAnalysisWidget,
    createAlertsManagerWidget,
    createWellCorrelationWidget,
  } = actualTab;

  const resolveWidgetGroupOptions = (wellId: number | null): TCreateWidgetOptions | undefined => {
    if (wellId === null) {
      return undefined;
    }

    const existedGroupId = groupSelectStore.groupManager.groups.find(
      (group) => groupSelectStore.groupManager.getWellIdInGroup(group.id) === wellId,
    )?.id;

    if (existedGroupId) {
      return { groupId: existedGroupId };
    }

    const selectedWellId = wellListPanelState.selectedWellId ?? null;
    const selectedGroupId = wellListPanelState.groupId ?? null;

    if (wellId === selectedWellId) {
      return selectedGroupId ? { groupId: selectedGroupId } : undefined;
    }

    const freeGroupId = groupSelectStore.groupManager.groups.find((group) => {
      if (selectedGroupId && selectedWellId !== null && group.id === selectedGroupId) {
        return false;
      }

      return groupSelectStore.groupManager.getWidgetsByGroupId(group.id).length === 0;
    })?.id;

    return freeGroupId ? { groupId: freeGroupId } : undefined;
  };

  const extraWellButtons = (well: TEnrichedWell) => {
    const options = resolveWidgetGroupOptions(well.id);

    return (
      <ContextMenuButton
        id={well.id}
        options={options}
        onCreateWellLogsWidget={createWellLogsWidget}
        onCreateTemplate={(template, wellId) => onCreateNewTabAccept?.(template, wellId, well.name)}
        onCreateOperationalParamsWidget={(wellIndexType, wellId, enableSound, options) =>
          createOperationalParametersWidget(wellIndexType, wellId, enableSound, null, options)
        }
        onCreateWellAnalyseWidget={(wellId, options) => createWellAnalyseWidget(wellId, null, options)}
        onCreateDirectionalDrillingWidget={(wellId, options) => createDirectionalDrillingWidget(wellId, null, options)}
        onCreateImageBroadcastWidget={(wellId, options) => createImageBroadcastWidget(wellId, null, options)}
        onCreateVideoBroadcastWidget={(wellId, options) => createVideobroadcastWidget(wellId, null, options)}
        onCreateDayDepthChartWidget={(wellId, options) => createDayDepthChartWidget(wellId, null, options)}
        onCreateHeatmapWidget={(wellId, options) => createHeatmapWidget(wellId, null, options)}
        onCreateDrillingMultiAnalysisWidget={(wellId, options) =>
          createDrillingMultiAnalysisWidget(wellId, null, options)
        }
        onCreateAlertsManagerWidget={(wellId, options) => createAlertsManagerWidget(wellId, null, options)}
        onCreateWellCorrelationWidget={(wellId, options) => createWellCorrelationWidget(wellId, null, options)}
      />
    );
  };

  return (
    <div ref={ref} className={styles.popupWrapper}>
      <div style={{ display: 'flex', padding: 8, lineHeight: '16px' }}>
        <GroupSelectForWellList
          groupSelectStore={groupSelectStore}
          onChangeGroup={(v) => wellListPanelState.updateWellListPanelState({ groupId: v?.id })}
          selectedGroup={groupSelectStore.groupManager.groups.find((group) => group.id === wellListPanelState?.groupId)}
          onOpenChange={setIsGroupSelectOpen}
          open={isGroupSelectOpen}
        />
        <div>{t('wellList:title')}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <PinWidgetButton
            isPinned={isPinned}
            onClick={() => wellListPanelState.updateWellListPanelState({ isPinned: !isPinned })}
          />
          {!widgets.find((w) => w instanceof FiltersWidgetEntity) && <FilterButton onClick={createFilterWidget} />}
        </div>
      </div>
      <WellListPanel
        store={actualTab.wellListPanelStore}
        theme={theme}
        language={language}
        refAt={refAt}
        extraWellButtons={extraWellButtons}
        onWellSelect={(well, options) => {
          wellListPanelState.updateWellListPanelState({ selectedWellId: well?.id });

          const isGroupIdChange = hasSelectReason(options) && options.reason === 'groupIdChange';

          if (!wellListPanelState.isPinned && !isGroupIdChange) {
            onClosePopup();
          }
        }}
        onFilterTypeChange={(v) => wellListPanelState.updateWellListPanelState({ filterBy: v })}
        onSortingTypeChange={(v) => wellListPanelState.updateWellListPanelState({ sorting: v })}
        onGroupingTypeChange={(v) => wellListPanelState.updateWellListPanelState({ groupBy: v })}
        onActiveKeysChange={(v) => wellListPanelState.updateWellListPanelState({ activeKeys: v })}
        onSearchValueChange={(v) => wellListPanelState.updateWellListPanelState({ searchValue: v })}
      />
    </div>
  );
});
